import { useSyncExternalStore } from 'react';

import { logReadFailure } from '../lib/net/logReadFailure';
import { startPeriodicRead } from '../lib/net/periodicRead';
import { SCHEDULED_CHANGED_EVENT } from '../lib/relations/scheduledChanged';
import type { ScheduledMailSend } from '../lib/relations/scheduledSend';
import { listScheduledSends } from '../lib/relations/scheduledSendApi';

/** A held mail is a notification too; it is re-read now and then while a page is open. */
const POLL_MS = 120_000;

interface HeldState {
  held: ScheduledMailSend[];
  /** The first read answered (an empty list before it is not « nothing held »). */
  loaded: boolean;
  /** The last read failed: a mail missing from `held` may only be unread, not gone. */
  failed: boolean;
  /** A retry asked by hand is under way (« Réessayer » says so instead of doing nothing visible). */
  reading: boolean;
}

let state: HeldState = { held: [], loaded: false, failed: false, reading: false };
/** Only the latest read answers: an older, slower one never overwrites it. */
let latestRead = 0;
const listeners = new Set<() => void>();
let stop: (() => void) | null = null;

function emit(next: HeldState) {
  state = next;
  for (const listener of listeners) listener();
}

async function load() {
  const read = ++latestRead;
  try {
    const sends = await listScheduledSends(null);
    if (read !== latestRead) return;
    emit({ held: sends.filter((s) => s.status === 'held'), loaded: true, failed: false, reading: false });
  } catch (err) {
    if (read !== latestRead) return;
    logReadFailure('travail', 'held mails unreadable', err);
    emit({ ...state, loaded: true, failed: true, reading: false });
  }
}

/**
 * Re-read now (« Réessayer »), for every surface at once. Only this one says « reading »: the
 * background poll does not re-render the page twice for it. A second click while it reads does nothing.
 */
export function reloadHeldMails(): void {
  if (state.reading) return;
  emit({ ...state, reading: true });
  void load();
}

function start() {
  void load();
  const stopPolling = startPeriodicRead({ everyMs: POLL_MS, read: () => void load() });
  const onChanged = () => void load();
  window.addEventListener(SCHEDULED_CHANGED_EVENT, onChanged);
  stop = () => {
    stopPolling();
    window.removeEventListener(SCHEDULED_CHANGED_EVENT, onChanged);
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stop?.();
      stop = null;
    }
  };
}

/**
 * The scheduled mails held at their time (« Mail programmé retenu », PLAN-BUREAU lot 6): the client
 * wrote since, a reply already left, Gmail refused, it would have left too late… They come back to
 * « T'attend » until Gilles sends, reschedules or cancels them. Read from the single reader of
 * scheduled sends (`list_scheduled_mail_sends`, every live send), kept to the held ones — once for
 * the whole app (the header's badge and the Bureau share it, like `useWaitingSignal`).
 */
export function useHeldMailsState(): HeldState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

export function useHeldMails(): ScheduledMailSend[] {
  return useHeldMailsState().held;
}
